import {
  BLOCKS_PER_SECTION,
  CHUNK_X,
  CHUNK_Z,
  NIBBLES_PER_SECTION,
  SECTION_COUNT,
  SECTION_Y,
  WORLD_MAX_Y,
  WORLD_MIN_Y,
  sectionIndex,
} from '../constants.ts';
import { AIR_STATE, isAir, lightFilter } from '../registry/blocks.ts';

/**
 * A 16x16x16 slice of a chunk column.
 *
 * Sections are allocated lazily: a column that is mostly empty space costs
 * almost nothing, which is what makes a 384-block-tall world affordable in a
 * browser tab. Light is stored as nibbles, matching both vanilla's layout and
 * the wire format, so chunk packets need no repacking.
 */
export class Section {
  readonly blocks: Uint16Array;
  readonly skyLight: Uint8Array;
  readonly blockLight: Uint8Array;
  /** Number of non-air blocks; when it hits zero the section can be freed. */
  nonAir = 0;
  /** Number of blocks that tick randomly (crops, grass spread, fire). */
  randomTickable = 0;

  constructor(blocks?: Uint16Array, skyLight?: Uint8Array, blockLight?: Uint8Array) {
    this.blocks = blocks ?? new Uint16Array(BLOCKS_PER_SECTION);
    this.skyLight = skyLight ?? new Uint8Array(NIBBLES_PER_SECTION);
    this.blockLight = blockLight ?? new Uint8Array(NIBBLES_PER_SECTION);
    if (blocks) this.recount();
  }

  recount(): void {
    let n = 0;
    for (let i = 0; i < BLOCKS_PER_SECTION; i++) {
      if (!isAir(this.blocks[i]!)) n++;
    }
    this.nonAir = n;
  }

  get(idx: number): number {
    return this.blocks[idx]!;
  }

  set(idx: number, state: number): void {
    const prev = this.blocks[idx]!;
    if (prev === state) return;
    const wasAir = isAir(prev);
    const isNowAir = isAir(state);
    if (wasAir && !isNowAir) this.nonAir++;
    else if (!wasAir && isNowAir) this.nonAir--;
    this.blocks[idx] = state;
  }

  getSky(idx: number): number {
    const b = this.skyLight[idx >> 1]!;
    return (idx & 1) === 0 ? b & 0xf : b >> 4;
  }

  setSky(idx: number, value: number): void {
    const i = idx >> 1;
    const b = this.skyLight[i]!;
    this.skyLight[i] = (idx & 1) === 0 ? (b & 0xf0) | value : (b & 0x0f) | (value << 4);
  }

  getBlockLight(idx: number): number {
    const b = this.blockLight[idx >> 1]!;
    return (idx & 1) === 0 ? b & 0xf : b >> 4;
  }

  setBlockLight(idx: number, value: number): void {
    const i = idx >> 1;
    const b = this.blockLight[i]!;
    this.blockLight[i] = (idx & 1) === 0 ? (b & 0xf0) | value : (b & 0x0f) | (value << 4);
  }

  fillSky(value: number): void {
    this.skyLight.fill(value | (value << 4));
  }

  fillBlockLight(value: number): void {
    this.blockLight.fill(value | (value << 4));
  }

  isEmpty(): boolean {
    return this.nonAir === 0;
  }
}

export type BlockEntityData = Record<string, unknown>;

export interface BlockEntity {
  /** Kind name, matching `BlockDef.entity`. */
  kind: string;
  /** World coordinates. */
  x: number;
  y: number;
  z: number;
  data: BlockEntityData;
}

/** Chunk lifecycle: generated -> populated -> lit -> ready to send. */
export type ChunkStatus = 'empty' | 'terrain' | 'features' | 'lit' | 'ready';

export class Chunk {
  readonly sections: (Section | null)[] = new Array(SECTION_COUNT).fill(null);
  /** Highest non-air block +1 per column, used to seed skylight. */
  readonly heightmap = new Int16Array(CHUNK_X * CHUNK_Z).fill(WORLD_MIN_Y);
  /** Highest block that blocks motion, used for mob spawning. */
  readonly surfacemap = new Int16Array(CHUNK_X * CHUNK_Z).fill(WORLD_MIN_Y);
  readonly biomes = new Uint8Array(CHUNK_X * CHUNK_Z);
  readonly blockEntities = new Map<number, BlockEntity>();
  status: ChunkStatus = 'empty';
  /** Set when the chunk has changed since it was last written to disk. */
  dirty = false;
  /** Set when the chunk needs its light recomputed before being sent. */
  lightDirty = true;
  /** Tick at which the chunk was last accessed, for unload decisions. */
  lastAccess = 0;
  /** Server-side: players currently subscribed to this chunk. */
  readonly viewers = new Set<number>();

  readonly cx: number;
  readonly cz: number;

  constructor(cx: number, cz: number) {
    this.cx = cx;
    this.cz = cz;
  }

  sectionIndexForY(y: number): number {
    return (y - WORLD_MIN_Y) >> 4;
  }

  getSection(sy: number): Section | null {
    return this.sections[sy] ?? null;
  }

  getOrCreateSection(sy: number): Section {
    let s = this.sections[sy];
    if (!s) {
      s = new Section();
      // A fresh section above the terrain starts fully skylit; the lighting
      // engine corrects this when the column below turns out to be occluded.
      this.sections[sy] = s;
    }
    return s;
  }

  /** Local coordinates: x,z in 0..15, y in world space. */
  getState(x: number, y: number, z: number): number {
    if (y < WORLD_MIN_Y || y >= WORLD_MAX_Y) return AIR_STATE;
    const s = this.sections[(y - WORLD_MIN_Y) >> 4];
    if (!s) return AIR_STATE;
    return s.blocks[sectionIndex(x, y & 15, z)]!;
  }

  setState(x: number, y: number, z: number, state: number): void {
    if (y < WORLD_MIN_Y || y >= WORLD_MAX_Y) return;
    const sy = (y - WORLD_MIN_Y) >> 4;
    let s = this.sections[sy];
    if (!s) {
      if (state === AIR_STATE) return;
      s = this.getOrCreateSection(sy);
    }
    s.set(sectionIndex(x, y & 15, z), state);
    this.dirty = true;
    this.updateHeightmapOnSet(x, y, z, state);
  }

  /** Set without touching heightmaps — used by bulk worldgen writes. */
  setStateRaw(x: number, y: number, z: number, state: number): void {
    if (y < WORLD_MIN_Y || y >= WORLD_MAX_Y) return;
    const sy = (y - WORLD_MIN_Y) >> 4;
    let s = this.sections[sy];
    if (!s) {
      if (state === AIR_STATE) return;
      s = this.getOrCreateSection(sy);
    }
    s.set(sectionIndex(x, y & 15, z), state);
  }

  private updateHeightmapOnSet(x: number, y: number, z: number, state: number): void {
    const i = (z << 4) | x;
    const blocksLight = lightFilter(state) > 0;
    if (blocksLight) {
      if (y >= this.heightmap[i]!) this.heightmap[i] = y + 1;
    } else if (y === this.heightmap[i]! - 1) {
      // The top block was removed; walk down to find the new surface.
      let ny = y - 1;
      while (ny >= WORLD_MIN_Y && lightFilter(this.getState(x, ny, z)) === 0) ny--;
      this.heightmap[i] = ny + 1;
    }
  }

  recomputeHeightmaps(): void {
    for (let z = 0; z < CHUNK_Z; z++) {
      for (let x = 0; x < CHUNK_X; x++) {
        const i = (z << 4) | x;
        let h = WORLD_MIN_Y;
        let surf = WORLD_MIN_Y;
        for (let sy = SECTION_COUNT - 1; sy >= 0; sy--) {
          const s = this.sections[sy];
          if (!s || s.isEmpty()) continue;
          const baseY = WORLD_MIN_Y + sy * SECTION_Y;
          for (let ly = SECTION_Y - 1; ly >= 0; ly--) {
            const st = s.blocks[sectionIndex(x, ly, z)]!;
            if (st === AIR_STATE) continue;
            const y = baseY + ly;
            if (h === WORLD_MIN_Y && lightFilter(st) > 0) h = y + 1;
            if (surf === WORLD_MIN_Y && !isAir(st)) surf = y + 1;
            if (h !== WORLD_MIN_Y && surf !== WORLD_MIN_Y) break;
          }
          if (h !== WORLD_MIN_Y && surf !== WORLD_MIN_Y) break;
        }
        this.heightmap[i] = h;
        this.surfacemap[i] = surf;
      }
    }
  }

  getHeight(x: number, z: number): number {
    return this.heightmap[(z << 4) | x]!;
  }

  getSkyLight(x: number, y: number, z: number): number {
    if (y < WORLD_MIN_Y) return 0;
    if (y >= WORLD_MAX_Y) return 15;
    const s = this.sections[(y - WORLD_MIN_Y) >> 4];
    if (!s) return y >= this.getHeight(x, z) ? 15 : 0;
    return s.getSky(sectionIndex(x, y & 15, z));
  }

  setSkyLight(x: number, y: number, z: number, v: number): void {
    if (y < WORLD_MIN_Y || y >= WORLD_MAX_Y) return;
    this.getOrCreateSection((y - WORLD_MIN_Y) >> 4).setSky(sectionIndex(x, y & 15, z), v);
  }

  getBlockLight(x: number, y: number, z: number): number {
    if (y < WORLD_MIN_Y || y >= WORLD_MAX_Y) return 0;
    const s = this.sections[(y - WORLD_MIN_Y) >> 4];
    if (!s) return 0;
    return s.getBlockLight(sectionIndex(x, y & 15, z));
  }

  setBlockLight(x: number, y: number, z: number, v: number): void {
    if (y < WORLD_MIN_Y || y >= WORLD_MAX_Y) return;
    this.getOrCreateSection((y - WORLD_MIN_Y) >> 4).setBlockLight(sectionIndex(x, y & 15, z), v);
  }

  getBiome(x: number, z: number): number {
    return this.biomes[(z << 4) | x]!;
  }

  setBiome(x: number, z: number, id: number): void {
    this.biomes[(z << 4) | x] = id;
  }

  getBlockEntity(x: number, y: number, z: number): BlockEntity | undefined {
    return this.blockEntities.get(beKey(x, y, z));
  }

  setBlockEntity(be: BlockEntity): void {
    this.blockEntities.set(beKey(be.x & 15, be.y, be.z & 15), be);
    this.dirty = true;
  }

  removeBlockEntity(x: number, y: number, z: number): void {
    if (this.blockEntities.delete(beKey(x, y, z))) this.dirty = true;
  }

  /** Free sections that became empty, reclaiming memory after big excavations. */
  prune(): void {
    for (let sy = 0; sy < SECTION_COUNT; sy++) {
      const s = this.sections[sy];
      if (s && s.isEmpty()) {
        // Keep the section if it carries non-default light that neighbours read.
        let hasLight = false;
        for (let i = 0; i < NIBBLES_PER_SECTION; i++) {
          if (s.blockLight[i] !== 0) {
            hasLight = true;
            break;
          }
        }
        if (!hasLight) this.sections[sy] = null;
      }
    }
  }
}

/** Key for the block-entity map: local x/z plus absolute y. */
export function beKey(lx: number, y: number, lz: number): number {
  return ((y - WORLD_MIN_Y) << 8) | (lz << 4) | lx;
}

/** Read-write access to a world's blocks, independent of client/server. */
export interface BlockView {
  getState(x: number, y: number, z: number): number;
  setState(x: number, y: number, z: number, state: number): void;
  getBlockLight(x: number, y: number, z: number): number;
  getSkyLight(x: number, y: number, z: number): number;
  getChunk(cx: number, cz: number): Chunk | undefined;
}
